import React, { useContext, useState } from "react";
import styled from "styled-components";
import { Modal, Form, Button } from "react-bootstrap";
import { Box, ButtonStyled, HeadingStyled, LabelStyled } from "../../styled/Elements";
import { useTranslation } from "react-i18next";
import ValidatedInput from "../../common/inputFields/ValidatedInput";
import { useForm } from "react-hook-form";
import PhoneInputField from "../../common/inputFields/PhoneInput";
import VisitorApi from "../../../services/api/visitorApi";
import { toast } from "react-toastify";
import TimeSlotSelector from "../../common/timeSlotSelector/TimeSlotSelector";
import DateSelector from "../../common/dateSelector/DateSelector";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authContext";
import { requireAuthNavigate } from "../../../utils";

const parseTimeSlot = (timeValue) => {
  if (Array.isArray(timeValue)) {
    return timeValue[0] ?? "";
  }
  return timeValue ?? "";
};

const parseSelectedDate = (dateValue) => {
  if (Array.isArray(dateValue)) {
    return dateValue[0] ?? "";
  }
  return dateValue ?? "";
};

/** Combines YYYY-MM-DD + "09:00 AM" into ISO8601 for the API (matches mobile). */
const buildPreferredVisitAt = (dateValue, timeValue) => {
  const datePart = parseSelectedDate(dateValue);
  const timePart = parseTimeSlot(timeValue);
  if (!datePart || !timePart) {
    return null;
  }

  const match = timePart.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return null;
  }

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridiem = match[3].toUpperCase();

  if (meridiem === "AM") {
    if (hours === 12) hours = 0;
  } else if (hours !== 12) {
    hours += 12;
  }

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");

  return `${datePart}T${hh}:${mm}:00`;
};

const VisitScheduleModal = ({ show, onHide, listingRef }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const buildMessage = (formObj) => {
    const date = parseSelectedDate(formObj.date);
    const time = parseTimeSlot(formObj.time);
    const ref =
      listingRef !== undefined && listingRef !== null && listingRef !== ""
        ? String(listingRef)
        : null;
    const listingLine = ref
      ? `${t("schedule_visit_contact_listing")}: ${ref}`
      : `${t("schedule_visit_contact_listing")}: ${t("schedule_visit_contact_na")}`;
    return [
      t("schedule_visit_contact_intro"),
      listingLine,
      `${t("schedule_visit_contact_date")}: ${date || t("schedule_visit_contact_na")}`,
      `${t("schedule_visit_contact_time")}: ${time || t("schedule_visit_contact_na")}`,
    ].join("\n");
  };

  const onSubmit = async (formObj) => {
    if (
      !requireAuthNavigate(
        navigate,
        isAuthenticated,
        `${location.pathname}${location.search}`
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      const payload = {
        name: formObj.name,
        email: formObj.email,
        phone: formObj.phone,
        message: buildMessage(formObj),
      };

      const listingId = Number(listingRef);
      if (Number.isFinite(listingId) && listingId > 0) {
        payload.listing_id = listingId;
      }

      const preferredVisitAt = buildPreferredVisitAt(formObj.date, formObj.time);
      if (preferredVisitAt) {
        payload.preferred_visit_at = preferredVisitAt;
      }

      const { code, message } = await VisitorApi.contactUs(payload);
      if (code === 200) {
        toast.success(message);
        onHide();
        reset();
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      centered
      style={{ zIndex: 999999 }}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalWrapper>
          <Modal.Header closeButton className="px-0">
            <Box>
              <HeadingStyled size={""}>{t("schedule_visit")}</HeadingStyled>
            </Box>
          </Modal.Header>
          <Modal.Body className="px-0">
            <ValidatedInput
              name="name"
              label={"name"}
              type="input"
              fieldType="text"
              placeholder={"name"}
              className={"w-100 mb-0 combined flex-wrap p-0"}
              register={register}
              errors={errors}
              validationRules={{
                required: true,
              }}
            />

            <ValidatedInput
              name="email"
              label={"email"}
              type="input"
              fieldType="email"
              placeholder={"email"}
              className={"w-100 mb-0 combined flex-wrap p-0"}
              register={register}
              errors={errors}
              validationRules={{
                required: true,
              }}
            />

            <PhoneInputField
              name="phone"
              label={"phone"}
              placeholder={"phone"}
              className={"w-100 mb-0 combined flex-wrap p-0"}
              register={register}
              control={control}
              validationRules={{
                required: true,
              }}
            />

            <div className="my-4">
              <LabelStyled color="var(--text)" className="mb-2">{t("date")}</LabelStyled>
              <DateSelector
                selectedDates={watch("date")}
                setSelectedDates={(value) => setValue("date", value)}
              />
            </div>

            {/* Time Slot Selection */}
            <div>
              <LabelStyled color="var(--text)" className="mb-2">{t("time_slot")}</LabelStyled>
              <TimeSlotSelector
                selectedSlots={watch("time")}
                setSelectedSlots={(value) => setValue("time", value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="px-0">
            <Box className="d-flex justify-content-end gap-2">
              <ButtonStyled
                label={t("Cancel")}
                bg={"var(--bg)"}
                color={"var(--text)"}
                onClick={onHide}
              />
              <ButtonStyled
                type="submit"
                bg="var(--themeColorSecondary)"
                isLoading={loading}
                disabled={loading}
                label={t("submit")}
              />
            </Box>
          </Modal.Footer>
        </ModalWrapper>
      </Form>
    </Modal>
  );
};

export default VisitScheduleModal;

const ModalWrapper = styled.div`
  margin-left: 30px;
  margin-right: 30px;
  .btn-close {
    border: 1px solid var(--text3);
    border-radius: 50%;

    :hover {
      border-color: var(--text3);
    }
  }
  @media (max-width: 576px) {
    margin-left: 20px;
    margin-right: 20px;
  }
`;
